import React from 'react';
import { useLocation, useOutlet } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import {
  BoltIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { type ColumnDef, DataTable, type SortDirection } from '@oysterjs/ui/data-table';
import { SearchFilterInput, useFiltersFromLocalStorage } from '@oysterjs/ui/search-filter';

import {
  TriggerEventState,
  TriggerEventType,
  type TriggerEventListFieldsFragment,
  TriggerRunState
} from '~/api/graphql';
import { useOptionalCurrentAgencyUser } from '~/components/current-user';
import { LibraryListEmptyState } from '~/components/list/library-list-empty-state';
import { LibraryListLoading } from '~/components/list/library-list-loading';
import {
  compareText,
  EVENT_TYPE_LABELS,
  eventHref,
  EventStateBadge,
  eventSummary,
  EventTypeBadge,
  formatEventTimestamp,
  matchedRunCount,
  RunStateBadge,
  sortRows,
  triggerHref
} from './trigger-utils';
import {
  createTriggerExecutionFilterDefinitions,
  type TriggerExecutionFilter
} from './trigger-filters';

import { type HistoryEdge, useExecutionHistory } from './trigger-execution-history';
import { emailRunFailureLabel } from '~/api/data/triggers/email-run-error';

type ExecutionSortKey = 'event' | 'state' | 'type' | 'matched' | 'received';

type EmailRun = NonNullable<HistoryEdge['emailRun']>;

/**
 * One row: a generic trigger event, or a connected-mailbox run. The combined
 * connection keeps both existing representations; mailbox history queries
 * enforce frozen privacy (Agency viewers, Shared proven mailbox owners,
 * Private first owner) plus current workspace access. Without a workspace only
 * the first owner may read; same-agency admins may read all. No mail content.
 */
type ExecutionRow = {
  id: string;
  summary: string;
  /** What the State filter matches; a mailbox run's launch maps onto it. */
  state: TriggerEventState;
  eventType: TriggerEventType;
  matched: number;
  receivedAt: Date;
  href: string;
} & ({ event: TriggerEventListFieldsFragment } | { run: EmailRun });

const eventRow = (event: TriggerEventListFieldsFragment): ExecutionRow => ({
  id: `event:${event.id}`,
  summary: eventSummary(event),
  state: event.state,
  eventType: event.eventType,
  matched: matchedRunCount(event),
  receivedAt: new Date(event.receivedAt),
  href: eventHref(event.id),
  event
});

const RUN_FILTER_STATE: Record<TriggerRunState, TriggerEventState> = {
  [TriggerRunState.Launched]: TriggerEventState.Evaluated,
  [TriggerRunState.Queued]: TriggerEventState.Pending,
  [TriggerRunState.Failed]: TriggerEventState.Failed
};

const emailRunRow = (run: EmailRun, summary: string): ExecutionRow => ({
  id: `email-run:${run.id}`,
  summary,
  state: RUN_FILTER_STATE[run.state],
  eventType: TriggerEventType.EmailReceived,
  matched: 1,
  receivedAt: new Date(run.queuedAt),
  href: run.workspaceId
    ? `/app/workspaces/${run.workspaceId}`
    : triggerHref(run.triggerDefinitionId),
  run
});

/**
 * An evaluated event shows what its matched runs did, so a launched schedule or
 * forwarded-email run reads "Launched" like a mailbox run. Mixed outcomes across
 * several triggers, or no match, keep the event's own state.
 */
const EventOutcomeBadge = ({ event }: { event: TriggerEventListFieldsFragment }) => {
  const runState = event.runs[0]?.state;
  return event.state === TriggerEventState.Evaluated &&
    runState &&
    event.runs.every((run) => run.state === runState) ? (
    <RunStateBadge state={runState} />
  ) : (
    <EventStateBadge state={event.state} />
  );
};

const rowHref = (row: ExecutionRow) => row.href;

const sortExecutions = (
  rows: ExecutionRow[],
  key: ExecutionSortKey,
  direction: SortDirection
): ExecutionRow[] => {
  const compare = (a: ExecutionRow, b: ExecutionRow) => {
    switch (key) {
      case 'event':
        return compareText(a.summary, b.summary);
      case 'state':
        return compareText(a.state, b.state);
      case 'type':
        return compareText(EVENT_TYPE_LABELS[a.eventType], EVENT_TYPE_LABELS[b.eventType]);
      case 'matched':
        return a.matched - b.matched;
      case 'received':
        return a.receivedAt.getTime() - b.receivedAt.getTime();
    }
  };
  return sortRows(rows, compare, direction);
};

const EXECUTION_COLUMNS: ColumnDef<ExecutionRow, ExecutionSortKey>[] = [
  {
    id: 'event',
    header: 'Event',
    sortKey: 'event',
    cellClassName: 'max-w-md',
    renderCell: (row) => (
      <span
        className="block truncate text-sm font-medium text-neutral-900 dark:text-white"
        title={row.summary}
      >
        {row.summary}
      </span>
    )
  },
  {
    id: 'state',
    header: 'Status',
    sortKey: 'state',
    renderCell: (row) =>
      'run' in row ? (
        <div>
          <RunStateBadge state={row.run.state} />
          {emailRunFailureLabel(row.run) && (
            <p className="m-0 mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              {emailRunFailureLabel(row.run)}
            </p>
          )}
        </div>
      ) : (
        <EventOutcomeBadge event={row.event} />
      )
  },
  {
    id: 'type',
    header: 'Type',
    sortKey: 'type',
    headerClassName: 'hidden sm:table-cell',
    cellClassName: 'hidden sm:table-cell',
    renderCell: (row) => <EventTypeBadge eventType={row.eventType} />
  },
  {
    id: 'matched',
    header: 'Triggers Matched',
    sortKey: 'matched',
    headerClassName: 'hidden md:table-cell',
    cellClassName: 'hidden md:table-cell',
    renderCell: (row) => {
      const count = row.matched;
      return (
        <span
          className={
            count > 0
              ? 'text-sm text-neutral-700 dark:text-neutral-300'
              : 'text-sm text-neutral-400 dark:text-neutral-500'
          }
        >
          {count > 0 ? count : 'None'}
        </span>
      );
    }
  },
  {
    id: 'received',
    header: 'Received',
    sortKey: 'received',
    renderCell: (row) => (
      <span className="block truncate text-xs text-neutral-600 dark:text-neutral-400">
        {formatEventTimestamp(row.receivedAt)}
      </span>
    )
  }
];

export const WorkflowTriggerExecutionsPage = () => {
  const agencyUser = useOptionalCurrentAgencyUser();
  const location = useLocation();
  const outlet = useOutlet();
  const filterDefinitions = React.useMemo(() => createTriggerExecutionFilterDefinitions(), []);
  const { activeFilters, setActiveFilters, initialFilters, sortBy, sortDirection, setSort } =
    useFiltersFromLocalStorage<TriggerExecutionFilter, ExecutionSortKey>(
      'workflow_trigger_executions_default_view',
      filterDefinitions,
      { defaultSortBy: 'received', defaultSortDirection: 'desc' }
    );
  const history = useExecutionHistory(
    agencyUser?.agency?.id ?? '',
    activeFilters,
    agencyUser?.id ?? ''
  );
  const events = React.useMemo<ExecutionRow[]>(
    () =>
      history.edges.map((edge) =>
        edge.event
          ? { ...eventRow(edge.event), summary: edge.summary }
          : emailRunRow(edge.emailRun!, edge.summary)
      ),
    [history.edges]
  );
  // The default order is the server's full-precision merged keyset order.
  // Other column sorts apply only to already loaded rows, not continuation.
  const sortLoaded = React.useCallback(
    (rows: ExecutionRow[], key: ExecutionSortKey, direction: SortDirection) =>
      key === 'received'
        ? direction === 'desc'
          ? rows
          : [...rows].reverse()
        : sortExecutions(rows, key, direction),
    []
  );

  return (
    <>
      <div className="space-y-4">
        <SearchFilterInput
          filterDefinitions={filterDefinitions}
          onFiltersChange={setActiveFilters}
          initialFilters={initialFilters}
          placeholder="Filter by state, type, date, or search..."
        />

        <div className="flex items-center gap-3 text-sm text-neutral-600 dark:text-neutral-400">
          <button type="button" onClick={history.refresh} disabled={history.loading}>
            Refresh history
          </button>
          <span>Newest first by default; other sorts apply to loaded results.</span>
          <span role="status">
            {history.paused
              ? 'Automatic updates are paused while you browse older executions. Refresh history to return to the latest.'
              : 'The latest executions update automatically every 5 seconds.'}
          </span>
        </div>
        {history.error && (
          <div role="alert" className="text-sm text-red-600 dark:text-red-400">
            History is incomplete. Unable to load all sources.
            <button
              type="button"
              onClick={history.retry}
              disabled={history.loading}
              className="ml-2 underline"
            >
              Retry history
            </button>
          </div>
        )}
        {history.loading && events.length === 0 ? (
          <LibraryListLoading title="Loading executions..." />
        ) : history.error && events.length === 0 ? (
          <LibraryListEmptyState
            icon={ExclamationTriangleIcon}
            title="Unable to load executions"
            description="Retry to load complete history."
          />
        ) : events.length === 0 ? (
          <LibraryListEmptyState
            icon={activeFilters.length ? MagnifyingGlassIcon : BoltIcon}
            title={activeFilters.length ? 'No matching executions' : 'No executions yet'}
            description={
              activeFilters.length
                ? 'Try adjusting your search or filters.'
                : 'Executions appear here when an active trigger runs.'
            }
          />
        ) : (
          <div className="px-4 sm:px-6 rounded-lg bg-white shadow dark:bg-neutral-800">
            <DataTable
              data={events}
              columns={EXECUTION_COLUMNS}
              keyExtractor={(event) => event.id}
              rowHref={rowHref}
              tableClassName="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
              sortKey={sortBy}
              sortDirection={sortDirection}
              onSort={setSort}
              sortFn={sortLoaded}
            />
          </div>
        )}
        {events.length > 0 && (history.more || history.loading) && (
          <button type="button" onClick={history.loadMore} disabled={history.loading}>
            {history.loading ? 'Loading older executions...' : 'Load more'}
          </button>
        )}
        {events.length > 0 && !history.more && !history.error && !history.loading && (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            No older matching executions.
          </p>
        )}
      </div>

      <AnimatePresence mode="wait">
        {outlet && React.cloneElement(outlet, { key: location.pathname })}
      </AnimatePresence>
    </>
  );
};
